import { loadStripe } from "@stripe/stripe-js";
/* -----------------Globals--------------- */
import { useEffect, useMemo, useState } from "react";

/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import { Stack, useLocalSearchParams } from "expo-router";

/* -----------------Constants--------------- */

import { STRIPE_KEY } from "@constants/env";
import { pageViewTypes } from "@constants/tracking";
import { events } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { handleCreateCheckoutSession } from "@features/dashboard/helpers/purchase";
import { handleNavigateBack } from "@helpers/common";
import useAnalytics from "@hooks/useAnalytics";
import { useProductDetails } from "@hooks/useProduct";
import type { ProductType } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import Loader from "@memorang/ui/components/Loader";
/* -----------------Stripe--------------- */
import {
	EmbeddedCheckout,
	EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import DocumentTitle from "components/DocumentTitle";
import { useTranslation } from "react-i18next";

const CheckoutRoute = () => {
	const baseUrl = useAppStore((store) => store.app.links.baseUrl);
	const [clientSecret, setClientSecret] = useState<string | null>(null);

	const stripePromise = useMemo(() => {
		if (!STRIPE_KEY) {
			throw new Error("Stripe key not found");
		}
		return loadStripe(STRIPE_KEY);
	}, []);

	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.checkout,
			eventName: events.checkoutViewed,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams);
	const { t } = useTranslation();

	const { userLogicalId } = useAuthContext();

	const { productType, id, planId } = useLocalSearchParams<{
		productType: ProductType;
		id: string;
		planId?: string;
	}>();

	const { data: productDetails } = useProductDetails(
		productType,
		Number.parseInt(id),
	);

	useEffect(() => {
		const fetchDetailsAndCreateSession = async () => {
			if (id) {
				const numericId = Number.parseInt(id);

				if (productDetails) {
					const productPlans = productDetails.plans;

					const selectedPlan = productPlans.find(
						(plan) => plan.id === Number.parseInt(planId!),
					);

					const routeSegment = `purchase/${numericId}/success?duration=${selectedPlan?.duration}&interval=${selectedPlan?.interval}&amount=${selectedPlan?.amount}&currency=${selectedPlan?.currency}&planId=${selectedPlan?.id}`;
					const redirectUrl = `${baseUrl}/${routeSegment}`;

					const productDescription =
						"Unlock all items and maximize your test readiness";

					if (selectedPlan && userLogicalId) {
						const res = await handleCreateCheckoutSession({
							selectedPlan,
							productDescription,
							productId: productDetails.id,
							redirectUrl,
							productName: productDetails.name,
							userId: userLogicalId,
						});
						setClientSecret(res);
					}
				}
			}
		};

		fetchDetailsAndCreateSession();
	}, [id, planId, baseUrl, productDetails, userLogicalId]);

	if (!clientSecret) {
		return (
			<Box flex={1} justifyContent="center" alignItems="center" height="100%">
				<Loader />
			</Box>
		);
	}

	return (
		<>
			<DocumentTitle title="Checkout" />
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => {
						return (
							<CustomAppBar
								title={t("purchase.checkout.headerTitle")}
								onPressBack={() => {
									handleNavigateBack("/(protected)/(tabs)/home");
								}}
								hideCommandBar
							/>
						);
					},
				}}
			/>
			{clientSecret && (
				<EmbeddedCheckoutProvider
					stripe={stripePromise}
					options={{ clientSecret }}
				>
					<EmbeddedCheckout />
				</EmbeddedCheckoutProvider>
			)}
		</>
	);
};

export default CheckoutRoute;
